import { sb } from '../supabase';

// Growth Director, Phase 1 (awareness, no AI) — owner, 2026-11-02. Rule-based
// alerts, the daily snapshot, «وضع معبر» and the deals closed outside the app.
// Everything reads through is_growth_owner() in the database
// (20261102320000_growth_director_awareness.sql).

export const RULE_AR = {
  stuck_order: 'طلب عالق',
  shipping_stale: 'الشحن',
  fee_refund_due: 'استرداد 299',
  payment_problem: 'الدفع',
  refund_failed: 'الاسترداد',
  tracking_silent: 'التتبّع',
  pull_failed: 'سحب البيانات',
  cron_failed: 'المهام المجدولة',
  ads_over_pace: 'الإعلانات',
  ads_over_budget: 'الإعلانات',
  wa_failed: 'واتساب',
  report_missing: 'تقرير الصباح',
};

export const DEAL_STAGES = [
  ['lead', 'عميل محتمل'], ['negotiating', 'تفاوض'], ['won', 'تم الاتفاق'],
  ['in_progress', 'قيد التنفيذ'], ['delivered', 'تم التسليم'], ['lost', 'لم تتم'],
];
export const DEAL_SOURCES = [
  ['whatsapp', 'واتساب'], ['referral', 'إحالة'], ['ads', 'إعلانات'], ['social', 'تواصل اجتماعي'],
  ['direct', 'مباشر'], ['exhibition', 'معرض'], ['other', 'أخرى'],
];
export const COMMISSION_STATUSES = [['pending', 'متوقعة'], ['confirmed', 'مؤكدة'], ['received', 'مستلمة']];
export const labelOf = (pairs, key) => (pairs.find(([k]) => k === key) || [key, key])[1];

// Open alerts first — critical before warning, newest first — then the
// resolved ones, newest first.
export function splitAlerts(rows = []) {
  const rank = (a) => (a.severity === 'critical' ? 0 : 1);
  const newest = (a, b) => String(b.first_seen_at).localeCompare(String(a.first_seen_at));
  return {
    open: rows.filter((a) => !a.resolved_at).sort((a, b) => rank(a) - rank(b) || newest(a, b)),
    resolved: rows.filter((a) => a.resolved_at).sort((a, b) => String(b.resolved_at).localeCompare(String(a.resolved_at))),
  };
}

// Admin-facing only. Traders never see the tiers as percentages (owner, 2026-11-02).
export function feeTiersText(tiers) {
  if (!Array.isArray(tiers) || !tiers.length) return '—';
  const sorted = [...tiers].sort((a, b) => Number(a.from_sar) - Number(b.from_sar));
  return sorted.map((t) => {
    const pct = `${+(Number(t.rate) * 100).toFixed(2)}%`;
    return Number(t.from_sar) > 0 ? `${pct} من ${Number(t.from_sar).toLocaleString('en-US')} ر.س` : `${pct} من أول ريال`;
  }).join(' · ');
}

// Month-to-date ad spend in SAR. SAR as-is, USD at the 3.75 peg; anything else
// is not converted and is named, never silently dropped.
export function adsMonthToDate(list = []) {
  let sar = 0; const unconverted = new Set();
  for (const r of list) {
    const v = Number(r.spend) || 0;
    if (r.currency === 'SAR') sar += v;
    else if (r.currency === 'USD') sar += v * 3.75;
    else if (v) unconverted.add(r.currency || '?');
  }
  return { sar: Math.round(sar * 100) / 100, unconverted: [...unconverted] };
}

// Where spend should be by today if the month's plan is spread evenly. The
// alert rule in the database fires above expected × 1.10.
export function adsPace(planSar, mtdSar, day, daysInMonth) {
  if (!planSar || !day || !daysInMonth) return null;
  const expected = Math.round((planSar * day / daysInMonth) * 100) / 100;
  return { expected, ahead: mtdSar > expected * 1.10, over: mtdSar > planSar };
}

// Commission sums by status, leaving out deals that did not happen.
export function dealTotals(deals = []) {
  const t = { pending: 0, confirmed: 0, received: 0, value: 0, open: 0 };
  for (const d of deals) {
    if (d.stage === 'lost') continue;
    t[d.commission_status] = (t[d.commission_status] || 0) + (Number(d.commission_sar) || 0);
    t.value += Number(d.deal_value_sar) || 0;
    if (!['delivered'].includes(d.stage)) t.open += 1;
  }
  return t;
}

const sumObj = (o) => Object.values(o || {}).reduce((s, v) => s + (Number(v) || 0), 0);
const traffic = (m, k) => sumObj(Object.fromEntries(Object.entries(m?.traffic || {}).map(([p, x]) => [p, x?.[k]])));

// The lines the director page shows for one snapshot next to the day before.
export function snapshotLines(cur, prev) {
  const pick = [
    ['زيارات (جلسات)', (m) => traffic(m, 'sessions')],
    ['جلسات شاهدت منتجاً', (m) => traffic(m, 'product_view_sessions')],
    ['جلسات أضافت للسلة', (m) => traffic(m, 'cart_add_sessions')],
    ['جلسات بدأت الدفع', (m) => traffic(m, 'checkout_sessions')],
    ['طلبات متجر مدفوعة', (m) => m?.market?.orders_paid],
    ['مبيعات المتجر (ر.س)', (m) => m?.market?.paid_total_sar],
    ['رسوم الخدمة (ر.س)', (m) => m?.market?.service_fee_sar],
    ['رسوم 299 مدفوعة', (m) => m?.request_fees?.paid],
    ['طلبات جديدة (RFQ وغيرها)', (m) => sumObj(m?.requests?.created_by_kind)],
    ['عروض أسعار جديدة', (m) => m?.requests?.offers_created],
    ['تجار جدد', (m) => m?.accounts?.new_buyers],
    ['موردون جدد', (m) => m?.accounts?.new_suppliers],
    ['منتجات أضيفت', (m) => (Number(m?.catalog?.supplier_products_added) || 0) + (Number(m?.catalog?.factory_products_added) || 0)],
    ['دفعات فشلت', (m) => (Number(m?.payments?.failed) || 0) + (Number(m?.payments?.mismatch) || 0)],
    ['واتساب فشل', (m) => m?.whatsapp?.failed],
    // suppliers in their dashboard — not trader demand, counted apart (2026-11-03)
    ['جلسات موردين (خارج الطلب)', (m) => m?.supplier_activity?.sessions],
  ];
  return pick.map(([label, f]) => ({ label, value: f(cur) ?? 0, prev: prev ? (f(prev) ?? 0) : null }));
}

// ── data ─────────────────────────────────────────────────────────────────────
export async function fetchAlerts() {
  const since = new Date(Date.now() - 30 * 86400000).toISOString();
  const { data, error } = await sb.from('growth_alerts')
    .select('id, rule, severity, title_ar, rows, link, first_seen_at, last_seen_at, resolved_at, acknowledged_at, email_state')
    .or(`resolved_at.is.null,resolved_at.gte.${since}`)
    .order('first_seen_at', { ascending: false }).limit(300);
  if (error) throw error;
  return data || [];
}
export async function ackAlert(id) {
  const { error } = await sb.rpc('growth_alert_ack', { p_id: id });
  if (error) throw error;
}
export async function fetchSnapshots(limit = 2) {
  const { data, error } = await sb.from('growth_daily_snapshot').select('day, metrics, computed_at').order('day', { ascending: false }).limit(limit);
  if (error) throw error;
  return data || [];
}
export async function fetchContext() {
  const { data, error } = await sb.from('growth_director_context').select('*').order('sort');
  if (error) throw error;
  return data || [];
}
export async function saveContext(row) {
  const { key, kind, title_ar, body, value_num, unit_ar, sort } = row;
  const { error } = await sb.from('growth_director_context').upsert({ key, kind, title_ar, body, value_num, unit_ar, sort }, { onConflict: 'key' });
  if (error) throw error;
}
export async function removeContext(key) {
  const { error } = await sb.from('growth_director_context').delete().eq('key', key);
  if (error) throw error;
}
export async function fetchLiveValues() {
  const { data, error } = await sb.rpc('growth_director_live_values');
  if (error) throw error;
  return data || {};
}
export async function fetchDeals() {
  const { data, error } = await sb.from('growth_offline_deals').select('*').order('opened_on', { ascending: false }).order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}
const DEAL_FIELDS = ['client_name', 'deal_value_sar', 'commission_sar', 'commission_status', 'source', 'stage', 'opened_on', 'closed_on', 'notes'];
export async function saveDeal(deal) {
  const row = Object.fromEntries(DEAL_FIELDS.map((k) => [k, deal[k] === '' ? null : deal[k] ?? null]));
  for (const k of ['deal_value_sar', 'commission_sar']) row[k] = row[k] == null ? null : Number(row[k]);
  const q = deal.id ? sb.from('growth_offline_deals').update(row).eq('id', deal.id) : sb.from('growth_offline_deals').insert(row);
  const { error } = await q;
  if (error) throw error;
}
// ── Phase 2b: the morning report (20261103010000, edge function director-run) ──
export async function fetchReports(limit = 14) {
  const { data, error } = await sb.from('growth_ai_reports').select('id, kind, day, content, text_ar, created_at, delivered_whatsapp_at')
    .eq('kind', 'morning').order('day', { ascending: false }).limit(limit);
  if (error) throw error;
  return data || [];
}
export async function fetchDirectorState() {
  const [s, spend, runs] = await Promise.all([
    sb.from('growth_director_settings').select('monthly_cap_usd, alert_pct, soft_pct, morning_enabled, whatsapp_enabled, chat_model').eq('id', 1).single(),
    sb.rpc('growth_ai_month_spend'),
    sb.from('growth_ai_audit').select('at, action, detail').like('action', 'run:%').order('at', { ascending: false }).limit(1),
  ]);
  for (const r of [s, spend, runs]) if (r.error) throw r.error;
  return { settings: s.data, spend: Number(spend.data) || 0, lastRun: (runs.data || [])[0] || null };
}
export async function setMorningEnabled(on) {
  const { error } = await sb.from('growth_director_settings').update({ morning_enabled: !!on }).eq('id', 1);
  if (error) throw error;
}
// Phase 3 (2026-11-03): the WhatsApp switch and the conversation model.
export async function setWhatsappEnabled(on) {
  const { error } = await sb.from('growth_director_settings').update({ whatsapp_enabled: !!on }).eq('id', 1);
  if (error) throw error;
}
export async function setChatModel(m) {
  const { error } = await sb.from('growth_director_settings').update({ chat_model: m === 'opus' ? 'opus' : 'sonnet' }).eq('id', 1);
  if (error) throw error;
}
// One thread for WhatsApp and Growth OS, oldest first.
export async function fetchConversation(limit = 40) {
  const { data, error } = await sb.from('growth_ai_messages').select('id, at, channel, direction, kind, body, ref')
    .in('kind', ['text', 'button', 'template', 'fixed', 'voice']).order('at', { ascending: false }).limit(limit);
  if (error) throw error;
  return (data || []).reverse();
}
export async function sendChat(text) {
  const { data, error } = await sb.functions.invoke('director-chat', { body: { text } });
  if (!error) return data;
  let body = null;
  try { body = await error.context?.json?.(); } catch { /* not JSON */ }
  return body || { ok: false, error: error.message };
}
const RUN_ERR_AR = {
  too_soon: 'تشغيل واحد كل 10 دقائق — جرّب بعد قليل.',
  budget_reached: 'ميزانية الشهر اكتملت — لا استدعاءات حتى تُرفع.',
  forbidden: 'هذا الزر للمالك فقط.',
  not_configured: 'الدالة غير مهيأة (سر ناقص).',
  anthropic_error: 'تعذّر الاتصال بـ Claude — سُجّل الخطأ.',
  not_end_turn: 'الرد لم يكتمل أو رُفض — لم يُحفظ تقرير.',
  invalid_report: 'الرد لم يطابق شكل التقرير — لم يُحفظ تقرير.',
};
export const runErrorText = (code) => RUN_ERR_AR[code] || `تعذّر التشغيل (${code || 'خطأ'})`;
// The «شغّل الآن» button: the function checks is_growth_owner() itself.
export async function runMorningNow() {
  const { data, error } = await sb.functions.invoke('director-run', { body: { trigger: 'manual' } });
  if (!error) return data;
  let body = null;
  try { body = await error.context?.json?.(); } catch { /* not JSON */ }
  return body || { ok: false, error: error.message };
}

export async function removeDeal(id) {
  const { error } = await sb.from('growth_offline_deals').delete().eq('id', id);
  if (error) throw error;
}
